/**
 * critique-export-flow.ts — the in-editor, REVIEWER-ONLY "Export as critique" action
 * (Epic gridline-marry-refinement Phase J5b).
 *
 * A THIN sibling of `runGroundTruthExport` (`ground-truth-export-flow.ts`), which is
 * itself a thin sibling of "Download edited PDF". Same browser-download plumbing;
 * differing ONLY in the payload — it emits a `CritiqueRecord` (a `recovered` + a
 * `corrected` `GroundTruthDoc` + the typed diff between them) instead of a single
 * `GroundTruthDoc`. It REUSES `serializeGroundTruth` for BOTH payloads (no 2nd GT
 * serializer — INV-CRIT-REUSE-EXPORT, enforced by lint-critique-reuse-export.py) and
 * the pure `diffAndVerdict` differ for the delta + oracle. No server endpoint, no new
 * render path (design §4).
 *
 * OPT-IN / REVIEWER-MODE, OFF BY DEFAULT (user directive 260908). This action is a
 * corpus-authoring tool for reviewers, NOT part of a regular user's editor. It is
 * gated by `isReviewerModeEnabled` (a NON-prod env AND an explicit opt-in signal) —
 * both false by default. A regular user's GT export path (`runGroundTruthExport`) is
 * UNCHANGED and byte-identical: this module touches it not at all, and emits a
 * `CritiqueRecord` only when a reviewer explicitly invokes THIS action.
 *
 * THE ORACLE (INV-CRIT-ORACLE). The record's oracle is the human correction
 * (`corrected` + `derivedVerdict`); `judgeProvenance` is always null here (v1 defers
 * judge-verdict-attach, §4(4)).
 *
 * DOM-free core: no `editor-state` singleton import; all DOM/state coupling is
 * INJECTED via `CritiqueExportDeps` (mirrors `GroundTruthExportDeps`), so the core is
 * unit-testable without the editor DOM scaffold.
 *
 * @module editor/critique-export-flow
 */
import { log } from "./log.js";
import { serializeGroundTruth, isNonProdEnvLabel, } from "./ground-truth-export-flow.js";
import { diffAndVerdict } from "./gt-diff.js";
import { CRITIQUE_SCHEMA_VERSION } from "./critique-types.js";
import { DEFAULT_PARTITION, stampedCritiqueFilename } from "./critique-corpus.js";
// ─── ULID (Crockford base32, 26 chars): time-ordered stable critique id ──────
const _CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/**
 * A ULID: 48-bit millisecond timestamp (10 chars) + 80-bit randomness (16 chars),
 * Crockford base32. Time-ordered so a lexical sort of critiqueIds is chronological.
 * Touches `crypto.getRandomValues` at call time (not module scope). Injected via
 * `deps.newCritiqueId` so the export core stays deterministic in tests.
 */
export function generateUlid(now = new Date()) {
    let ts = now.getTime();
    const time = [];
    for (let i = 9; i >= 0; i--) {
        time[i] = _CROCKFORD[ts % 32] ?? "0";
        ts = Math.floor(ts / 32);
    }
    const rand = new Uint8Array(10);
    crypto.getRandomValues(rand);
    const randChars = [];
    for (let i = 0; i < 16; i++) {
        // 16 base32 chars from 10 random bytes: take 5 bits per char.
        const bitOffset = i * 5;
        const byteIndex = Math.floor(bitOffset / 8);
        const bitInByte = bitOffset % 8;
        const hi = rand[byteIndex] ?? 0;
        const lo = rand[byteIndex + 1] ?? 0;
        const val = ((hi << 8) | lo) >> (11 - bitInByte);
        randChars.push(_CROCKFORD[val & 31] ?? "0");
    }
    return time.join("") + randChars.join("");
}
/**
 * Run one critique export: serialize the recovered baseline + the corrected IR (REUSE
 * the GT serializer), compute the typed diff + derived oracle, assemble the
 * `CritiqueRecord`, and download it as stamped JSON. Returns the produced record (or
 * null on a no-baseline / no-change / failure path) so tests can assert the payload.
 *
 * INV-CRIT-DELTA-TYPED: a no-op review (empty delta) is REJECTED — no record emitted.
 */
export async function runCritiqueExport(deps) {
    const current = deps.getIr();
    const baseline = deps.getBaselineIr();
    if (!current) {
        deps.announce("No document loaded to export as critique.");
        return null;
    }
    if (!baseline) {
        deps.announce("No pre-edit baseline captured — cannot export a critique for this document.");
        return null;
    }
    let sha = "";
    try {
        sha = await deps.computeSha();
    }
    catch (err) {
        log.warn("[editor] critique export: source SHA-256 unavailable:", err);
    }
    const now = (deps.now ?? (() => new Date()))();
    const provenance = {
        sourceDocSha256: sha,
        sourceDocName: deps.getSourceName() ?? "document.pdf",
        authoredBy: "editor",
        // Both payloads share ONE capture timestamp (deterministic; not two
        // independent `new Date()` calls) — recovered + corrected are one capture.
        authoredAtUtc: now.toISOString(),
        editorVersion: deps.getBuildVersion(),
        format: "pdf",
        pageCount: current.pages?.length ?? null,
    };
    let recovered;
    let corrected;
    try {
        // REUSE serializeGroundTruth for BOTH payloads (INV-CRIT-REUSE-EXPORT). The
        // recovered snapshot and the corrected structure share one serializer → the
        // corrected payload stays a valid GT target (INV-CRIT-CORRECTED-IS-GT).
        recovered = serializeGroundTruth(baseline, provenance, deps.pageCanvas);
        corrected = serializeGroundTruth(current, provenance, deps.pageCanvas);
    }
    catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        deps.announce(`Critique export failed: ${msg}`);
        if (deps.onError)
            deps.onError(`Critique export failed: ${msg}`);
        log.error("[editor] critique export failed:", err);
        return null;
    }
    const { delta, derivedVerdict } = diffAndVerdict(recovered, corrected);
    if (delta.length === 0) {
        // INV-CRIT-DELTA-TYPED: a critique with no typed structural change is not
        // machine-consumable — reject rather than emit an empty-delta record.
        deps.announce("No structural change to capture — edit the structure before exporting a critique.");
        return null;
    }
    const record = {
        critiqueId: deps.newCritiqueId(),
        schemaVersion: CRITIQUE_SCHEMA_VERSION,
        sourceDocSha256: sha,
        sourceDocName: deps.getSourceName() ?? "document.pdf",
        sourceJobId: deps.getSourceJobId ? deps.getSourceJobId() : null,
        capturedAtUtc: now.toISOString(),
        editorVersion: deps.getBuildVersion(),
        recovered,
        corrected,
        delta,
        derivedVerdict,
        annotations: deps.getAnnotations ? deps.getAnnotations() : [],
        // v1 defers judge-verdict-attach (§4(4)); the verdict is re-derivable per
        // judge version, and a stored verdict is never the oracle (INV-CRIT-ORACLE).
        judgeProvenance: null,
        partition: DEFAULT_PARTITION,
    };
    const filename = stampedCritiqueFilename(deps.getSourceName(), now);
    const blob = new Blob([JSON.stringify(record, null, 2)], { type: "application/json" });
    deps.download(blob, filename);
    deps.announce(`Exported critique: ${filename} (${delta.length} change${delta.length === 1 ? "" : "s"}).`);
    return record;
}
/**
 * Wire the reviewer-only "Export as critique" button to `runCritiqueExport`. A null
 * button (partial DOM / non-reviewer) no-ops.
 */
export function installCritiqueExport(deps) {
    if (!deps.button)
        return;
    deps.button.addEventListener("click", () => {
        void runCritiqueExport(deps);
    });
}
// ─── Reviewer-mode gate (opt-in, OFF by default) ─────────────────────────────
/**
 * True only when BOTH (a) the env is NON-prod (reuse `isNonProdEnvLabel` — the same
 * env-classification rule as the GT export gate, A.19) AND (b) an explicit opt-in
 * signal is set. Both false by default → critique capture is OFF for a regular user,
 * even in non-prod. This is the load-bearing opt-in gate (user directive 260908).
 */
export function isReviewerModeEnabled(envLabel, optIn) {
    return optIn && isNonProdEnvLabel(envLabel);
}
/**
 * Env+opt-in-gate the reviewer critique button: REMOVE it (not display:none) unless
 * reviewer mode is enabled, so it is genuinely absent otherwise. Mirrors
 * `applyExportButtonEnvGate`.
 */
export function applyCritiqueButtonReviewerGate(button, envLabel, optIn) {
    if (!button)
        return;
    if (!isReviewerModeEnabled(envLabel, optIn))
        button.remove();
}
